import React, { useState } from 'react';
import { styled } from '@mui/system';
import TextField from '@mui/material/TextField';
import { Lightbulb, Check, X } from 'lucide-react';
import {
  Card,
  Body,
  Muted,
  PrimaryButton,
  SecondaryButton,
  ExplainBox,
  FxLine
} from '../components/ui';
import { motion, fadeUp, shake, popIn } from '../components/motion';
import { scoreNumeric } from '../engine/scoring';
import { L, t } from '../strings';

const Tiles = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: 10,
  margin: '14px 0 18px',
  [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'repeat(2, 1fr)' }
}));

const Tile = styled('div')(({ theme, delay }) => ({
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.grey[100]}`,
  borderRadius: 12,
  padding: '12px 14px',
  ...motion(`${fadeUp} 360ms ease-out ${delay}ms both`)
}));

const TileLabel = styled('div')(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.textTertiary.main,
  fontFamily: 'Rubik-Regular'
}));
const TileValue = styled('div')(() => ({
  fontSize: 20,
  lineHeight: '28px',
  fontWeight: 600,
  fontFamily: 'Unbounded',
  marginTop: 4,
  fontVariantNumeric: 'tabular-nums'
}));

const Shake = styled('div')(() => ({
  '&.shake': motion(`${shake} 380ms ease-in-out`)
}));

const HintBox = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 10,
  alignItems: 'flex-start',
  padding: '10px 14px',
  borderRadius: 12,
  backgroundColor: theme.palette.secondary.pale,
  border: `1px solid ${theme.palette.secondary.light}`,
  ...motion(`${fadeUp} 300ms ease-out both`),
  '& svg': { color: '#b8960f', flexShrink: 0, marginTop: 2 }
}));

const Versus = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 10,
  marginTop: 12,
  maxWidth: 420,
  [theme.breakpoints.down('sm')]: { maxWidth: 'none' }
}));

const VsTile = styled('div')(({ tone, delay }) => ({
  padding: '10px 14px',
  borderRadius: 12,
  backgroundColor:
    tone === 'ok'
      ? 'rgba(121, 201, 18, 0.10)'
      : tone === 'bad'
        ? 'rgba(211, 47, 47, 0.08)'
        : '#F5F5F5',
  ...motion(`${popIn} 340ms ease-out ${delay}ms both`)
}));

const InputRow = styled('div')(() => ({
  display: 'flex',
  gap: 10,
  alignItems: 'center',
  flexWrap: 'wrap',
  marginTop: 8
}));

const parse = (s) => {
  const n = String(s).trim().replace(/\s/g, '').replace(',', '.');
  return n === '' ? NaN : Number(n);
};

export default function NumericStep({ step, answered, onSubmit, kpiLabels }) {
  const [value, setValue] = useState('');
  const [hint, setHint] = useState(false);
  const [error, setError] = useState('');
  const [shakeKey, setShakeKey] = useState(0);

  const submit = () => {
    const v = parse(value);
    if (!Number.isFinite(v)) {
      setError(t('yourAnswer'));
      setShakeKey((k) => k + 1);
      return;
    }
    const ok = scoreNumeric(v, step.answer, step.tolerance);
    onSubmit({
      score: ok ? (hint ? 0.5 : 1) : 0,
      fx: ok ? step.fx?.ok : step.fx?.fail,
      value: v,
      ok,
      usedHint: hint
    });
  };

  return (
    <Card>
      <Body>{L(step.text)}</Body>
      <Tiles>
        {step.data.map((d, i) => (
          <Tile key={i} delay={i * 70}>
            <TileLabel>{L(d.label)}</TileLabel>
            <TileValue>{L(d.value)}</TileValue>
          </Tile>
        ))}
      </Tiles>
      <Body style={{ fontWeight: 500 }}>{L(step.question)}</Body>
      {!answered ? (
        <>
          <InputRow>
            <Shake key={shakeKey} className={shakeKey ? 'shake' : ''}>
              <TextField
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submit();
                }}
                placeholder="0"
                inputMode="decimal"
                error={!!error}
                helperText={error || ' '}
                size="small"
                sx={{ width: 160 }}
              />
            </Shake>
            <Muted style={{ marginBottom: 20 }}>{L(step.unit)}</Muted>
            <PrimaryButton onClick={submit} sx={{ marginBottom: '20px' }}>
              {t('check')}
            </PrimaryButton>
            {!hint && (
              <SecondaryButton
                size="small"
                onClick={() => setHint(true)}
                sx={{ marginBottom: '20px' }}>
                {t('hint')}
              </SecondaryButton>
            )}
          </InputRow>
          {hint && (
            <HintBox>
              <Lightbulb size={17} />
              <Muted style={{ color: '#4C4C4C' }}>{L(step.hint)}</Muted>
            </HintBox>
          )}
        </>
      ) : (
        <>
          <Versus>
            <VsTile tone={answered.ok ? 'ok' : 'bad'} delay={0}>
              <TileLabel>
                {answered.ok ? (
                  <Check size={12} style={{ verticalAlign: -1 }} />
                ) : (
                  <X size={12} style={{ verticalAlign: -1 }} />
                )}{' '}
                {t('yourAnswer')}
              </TileLabel>
              <TileValue>
                {answered.value} <small style={{ fontSize: 13 }}>{L(step.unit)}</small>
              </TileValue>
            </VsTile>
            <VsTile delay={120}>
              <TileLabel>{t('recommended')}</TileLabel>
              <TileValue>
                {step.answer} <small style={{ fontSize: 13 }}>{L(step.unit)}</small>
              </TileValue>
            </VsTile>
          </Versus>
          <ExplainBox ok={answered.ok} label={answered.ok ? t('closeEnough') : t('offTarget')}>
            {L(step.explain)}
            <FxLine fx={answered.fx} kpiLabels={kpiLabels} />
          </ExplainBox>
        </>
      )}
    </Card>
  );
}
